/**
 * HR's payroll balance upload (9 Oct 2026, item B).
 *
 * Choose INOVA's accrual balances report as it is (CSV or .xlsx), check it, read what it would
 * do, then confirm. Confirming sends the same file with the code the check returned; the API
 * refuses if anything changed in between, so what is loaded is exactly what HR read.
 *
 * Loading a file also moves each person's PTO lock to that file's "Updated To Date". A face, no
 * rules: every number here comes from the API.
 */
import { mdy, processedThrough, type PayrollDryRun, type PayrollLoaded } from "@scheduler/shared";
import { useState } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";
import { hours } from "../requests/format";

export function PayrollUpload() {
  const api = useSchedulerApi();
  const [file, setFile] = useState<File | null>(null);
  const [check, setCheck] = useState<PayrollDryRun | null>(null);
  const [loaded, setLoaded] = useState<PayrollLoaded | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function choose(next: File | null) {
    setFile(next);
    setCheck(null);
    setLoaded(null);
    setError(null);
  }

  async function run<T>(action: () => Promise<T>, then: (value: T) => void) {
    setBusy(true);
    setError(null);
    try {
      then(await action());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-pad">
      <h3>Payroll balances</h3>
      <p className="hint">
        Upload INOVA's accrual balances report as it is (CSV or Excel). Check it, read what it will do, then
        confirm. Loading it also locks each person's PTO up to the file's "Updated To Date".
      </p>

      <div className="row2">
        <label className="field">
          <span className="label">Report file</span>
          <input type="file" accept=".csv,.xlsx" onChange={(e) => choose(e.target.files?.[0] ?? null)} />
        </label>
        <div className="field">
          <span className="label" aria-hidden="true">&nbsp;</span>
          <button type="button" className="primary" disabled={!file || busy}
            onClick={() => file && void run(() => api.payrollDryRun(file), setCheck)}>
            {busy && !check ? "Checking…" : "Check file"}
          </button>
        </div>
      </div>

      {error ? <p className="error" role="alert">{error}</p> : null}

      {loaded ? (
        <p className="toast" role="status">
          Loaded {loaded.fileName}: {loaded.balancesInserted} balance{loaded.balancesInserted === 1 ? "" : "s"} added
          {loaded.balancesSkipped ? `, ${loaded.balancesSkipped} already on file` : ""}.
          {/* Two weeks before the file's date: INOVA dates it ahead of what payroll has processed (lock.ts). */}
          {loaded.anchors.length ? ` PTO is now locked through ${loaded.anchors.map((a) => mdy(processedThrough(a))).join(", ")}.` : ""}
        </p>
      ) : null}

      {check && !loaded ? <DryRun check={check} /> : null}

      {check && !loaded ? (
        <div className="acts">
          <button type="button" className="primary" disabled={!check.canConfirm || busy}
            onClick={() => file && void run(() => api.payrollConfirm(file, check.code), setLoaded)}>
            {busy ? "Loading…" : `Confirm and load ${check.matched.length} balance${check.matched.length === 1 ? "" : "s"}`}
          </button>
          {!check.canConfirm ? <span className="muted">Fix the problems above and check the file again.</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function DryRun({ check }: { check: PayrollDryRun }) {
  return (
    <div className="dryrun">
      {check.problems.length ? (
        <>
          <h4>Problems — nothing can be loaded</h4>
          <ul className="error">{check.problems.map((p) => <li key={p}>{p}</li>)}</ul>
        </>
      ) : null}

      <p>
        <strong>{check.matched.length}</strong> matched · <strong>{check.unmatched.length}</strong> not matched ·{" "}
        <strong>{check.noBalance.length}</strong> unlimited (not loaded) · <strong>{check.activeWithoutRow.length}</strong> active people not in the file
        {check.anchors.length ? (
          <> · Updated To Date <strong>{check.anchors.map(mdy).join(", ")}</strong>, so PTO locks through{" "}
            <strong>{check.anchors.map((a) => mdy(processedThrough(a))).join(", ")}</strong></>
        ) : null}
      </p>

      {check.unmatched.length ? (
        <>
          <h4>Not matched — these rows will not be loaded</h4>
          <table className="data">
            <thead><tr><th className="r">Row</th><th>Name in payroll</th><th>Employee Id (Payroll ID)</th><th>Why</th></tr></thead>
            <tbody>
              {check.unmatched.map((u) => (
                <tr key={u.row}><td className="num">{u.row}</td><td>{u.payrollName}</td><td className="num">{u.payrollId}</td><td>{u.reason}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {check.matched.length ? (
        <>
          <h4>Matched — these balances will be loaded</h4>
          <table className="data">
            <thead><tr><th className="r">Row</th><th>Person</th><th>Employee Id (Payroll ID)</th><th>Through</th><th className="r">Balance</th></tr></thead>
            <tbody>
              {check.matched.map((m) => (
                <tr key={m.row}>
                  <td className="num">{m.row}</td><td>{m.person.name}</td><td className="num">{m.payrollId}</td>
                  <td>{mdy(m.accruedThrough)}</td><td className="r">{hours(m.balanceHours)} h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {check.activeWithoutRow.length ? (
        <>
          <h4>Active people with no row in this file</h4>
          <p className="hint">People on unlimited PTO are usually left out of the report. That is expected.</p>
          <ul>{check.activeWithoutRow.map((a) => <li key={a.person.ajeraKey}>{a.person.name} <span className="muted">— {a.note}</span></li>)}</ul>
        </>
      ) : null}
    </div>
  );
}
